import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, PartyPopper } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface Option {
  id: string;
  text: string;
  image_url?: string;
}

interface MultipleChoiceProps {
  question: string;
  options: Option[];
  correctId: string;
  onAnswer: (selectedId: string, isCorrect: boolean) => void;
  disabled?: boolean;
}

const MultipleChoiceActivity = ({ question, options, correctId, onAnswer, disabled }: MultipleChoiceProps) => {
  const [selected, setSelected] = useState<string | null>(null);
  const [answered, setAnswered] = useState(false);

  const handleSelect = (id: string) => {
    if (answered || disabled) return;
    setSelected(id);
  };

  const handleSubmit = () => {
    if (!selected || answered) return;
    const isCorrect = selected === correctId;
    setAnswered(true);
    onAnswer(selected, isCorrect);
  };

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-bold text-foreground text-center">{question}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {options.map((opt) => {
          const isCorrect = answered && opt.id === correctId;
          const isWrong = answered && opt.id === selected && opt.id !== correctId;
          return (
            <motion.button
              key={opt.id}
              whileHover={!answered ? { scale: 1.03 } : {}}
              whileTap={!answered ? { scale: 0.97 } : {}}
              className={cn(
                "relative p-4 rounded-xl border-2 text-right transition-all",
                "flex items-center gap-3",
                !answered && selected === opt.id && "border-primary bg-primary/10 shadow-md",
                !answered && selected !== opt.id && "border-border hover:border-primary/50 hover:bg-muted/50",
                isCorrect && "border-green-500 bg-green-50 dark:bg-green-950/30",
                isWrong && "border-red-500 bg-red-50 dark:bg-red-950/30",
                answered && !isCorrect && !isWrong && "opacity-50"
              )}
              onClick={() => handleSelect(opt.id)}
              disabled={answered || disabled}
            >
              {opt.image_url && (
                <img src={opt.image_url} alt="" className="w-16 h-16 rounded-lg object-cover" />
              )}
              <span className="flex-1 font-medium text-sm">{opt.text}</span>
              <AnimatePresence>
                {isCorrect && (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-green-600">
                    <CheckCircle2 className="w-6 h-6" />
                  </motion.div>
                )}
                {isWrong && (
                  <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="text-red-500">
                    <XCircle className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          );
        })}
      </div>

      {!answered && (
        <div className="flex justify-center">
          <Button onClick={handleSubmit} disabled={!selected} className="px-8 gap-2">
            تأكيد الإجابة
          </Button>
        </div>
      )}

      <AnimatePresence>
        {answered && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "text-center p-4 rounded-xl",
              selected === correctId ? "bg-green-50 dark:bg-green-950/30" : "bg-red-50 dark:bg-red-950/30"
            )}
          >
            {selected === correctId ? (
              <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
                <PartyPopper className="w-5 h-5" />
                <span className="font-bold">أحسنت! إجابة صحيحة 🌟</span>
              </div>
            ) : (
              <div className="text-red-600 dark:text-red-400">
                <span className="font-bold">حاول مرة أخرى! </span>
                <span className="text-sm">الإجابة الصحيحة: {options.find(o => o.id === correctId)?.text}</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MultipleChoiceActivity;
